'use client'

import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Link from 'next/link'
import type { FC } from 'react'
import GetStartedButton from '../../../shared/components/GetStartedButton'
import MLVisaCardImage from '../../../shared/components/MLVisaCardImage'
import { useAnalytics } from '../../../shared/context/Analytics'
import type { CardArtAssetName } from '../../../shared/types'

interface CardProductDetailsBoxProps {
  header: string
  cardArt?: CardArtAssetName
  productName: string
  description: string
  valueProps: {
    key: string
    value?: string
  }[]
  ctaName: string
  inviteCode: string
  termsPath?: string
  learnMorePath: string
}

const CardProductDetailsBox: FC<CardProductDetailsBoxProps> = ({
  header,
  cardArt = 'greenlineV0',
  productName,
  description,
  valueProps,
  inviteCode,
  termsPath,
  learnMorePath,
  ctaName,
}) => {
  const { track, events } = useAnalytics()

  return (
    <div
      className="flex flex-wrap flex-col md:flex-row gap-4 py-8 px-8 rounded-lg border bg-white border-ink-200 h-full"
      data-testid={`card-product-details-box-${productName.replaceAll(' ', '-')}`}
      data-nosnippet
    >
      <div className="md:hidden">
        <Typography variant="h2" component="h3">
          {header}
        </Typography>
      </div>
      <div className="flex flex-col gap-2 flex-1 md:w-[50%]">
        <div className="rounded-lg p-1 pb-4 md:pb-2">
          <MLVisaCardImage cardArt={cardArt} width={250} height={156} />
        </div>
        <div className="md:hidden">
          <Typography variant="h3" component="h4">
            {productName} Visa
            <sup>®</sup>
          </Typography>
        </div>
        <div className="hidden md:block">
          <Typography variant="h4">
            {productName} Visa
            <sup>®</sup>
          </Typography>
        </div>
        <span>
          <Typography variant="p2" className="text-bold">
            {description}{' '}
            {termsPath && (
              <Link
                href={termsPath}
                className="text-sm"
                onClick={() =>
                  track?.(events.INTERACTION.ClickCta, { cta: `${ctaName} - See Rates & Terms` })
                }
              >
                See Rates & Terms.
              </Link>
            )}
          </Typography>
        </span>
      </div>
      <div className="flex flex-col gap-6 flex-1 md:w-[50%]">
        <div className="hidden md:block">
          <Typography variant="h3">{header}</Typography>
        </div>
        <div className="flex flex-col py-4 gap-4 border-t-2 border-b-2 border-ink-200">
          {valueProps.map((prop) => (
            <div key={prop.key} className="flex justify-between">
              <Typography variant="p2">{prop.key}</Typography>
              {prop.value && (
                <Typography variant="h5" className="font-bold text-right">
                  {prop.value}
                </Typography>
              )}
            </div>
          ))}
        </div>
        <div className="flex flex-col min-[1430px]:flex-row gap-1">
          <GetStartedButton
            inviteCode={inviteCode}
            id={`application-button-${inviteCode}`}
            buttonText="See if I'm Approved"
            ctaName={`${ctaName} - See if I'm Approved`}
            buttonSize="small"
          />
          <Button
            id={`learn-more-${inviteCode}`}
            data-testid={`learn-more-${inviteCode}`}
            variant="text"
            color="primary"
            size="small"
            className="get-started w-full text-nowrap md:w-fit"
            href={learnMorePath}
            onClick={() => track?.(events.INTERACTION.ClickCta, { cta: `${ctaName} - Learn More` })}
          >
            Learn More
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CardProductDetailsBox
